// -----------------------------------------------------------------------------
// ProfileCard.jsx – nom sur 2 lignes max, avatar + badge intact
// -----------------------------------------------------------------------------
import React, { useState, useEffect, Fragment } from "react";
import {
  Card,
  CardActionArea,
  Chip,
  Typography,
  Avatar,
  Box,
  Divider,
  Tooltip,
  Rating,
} from "@mui/material";
import { getMedusaInspiredColor, getInitials } from "../../utils/utils";
import { getImageUrl } from "../../services/apiService";

const PRIMARY = "#2F4E8B";

export default function ProfileCard({ profile, selected, onClick }) {
  const [imageUrl, setImageUrl] = useState("");
  const [expanded, setExpanded] = useState(false);

  /* Avatar distant éventuel ------------------------------------------------ */
  useEffect(() => {
    (async () => {
      if (
        profile.localImagePath &&
        profile.localImagePath !== "Non spécifié"
      ) {
        try {
          setImageUrl(await getImageUrl(profile.localImagePath));
        } catch {/* ignore */ }
      }
    })();
  }, [profile.localImagePath]);

  /* Poste – raccourci / expansion ---------------------------------------- */
  const words       = profile.poste?.split(" ") || [];
  const isLongPoste = words.length > 4;
  const truncated   = words.slice(0, 4).join(" ");
  const displayPoste = expanded || !isLongPoste ? profile.poste : truncated;

  /* Compétences ----------------------------------------------------------- */
  const toArr = (v) =>
    typeof v === "string" && v.trim()
      ? v.split(",").map((x) => x.trim())
      : Array.isArray(v)
      ? v
      : [];
  const allSkills = toArr(profile.competences);
  const skills    = allSkills.slice(0, 5);
  const extra     = allSkills.length - skills.length;
// only show tarif when > 0, otherwise “Non spécifié”
const tarifDisplay = profile.tarif > 0
  ? `${profile.tarif}/j`
  : "Non spécifié";

  /* Lignes méta ----------------------------------------------------------- */
  const metaRows = [
    ["Poste",        displayPoste],
    ["Localisation", profile.localisation],
    ["Experience",   `${profile.inter_exp} years`],
    ["Tarif",        tarifDisplay],
  ];

  const borderColor = selected ? "#000" : "#e0e0e0";

  // Score de compatibilité (%) ---------------------------------------------
  const scoreCompat = Number(profile["Score de compatibilité (%)"]);
  const hasScore = !isNaN(scoreCompat);
  // Convert percentage (0-100) to 0-5 stars
  const starValue = hasScore ? (scoreCompat / 100) * 5 : 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        maxWidth: 340,
        border: `2px solid ${borderColor}`,
        borderRadius: 2,
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": { borderColor: "#000", boxShadow: "0 4px 14px rgba(0,0,0,0.2)" },
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <CardActionArea
        sx={{
          px: 6, py: 4,            /* 48 px latéraux / 32 px verticaux */
          height: "100%", display: "flex", flexDirection: "column",
        }}
      >
        {/* ------------------ Header ------------------ */}
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <Tooltip title={profile.summary || ""} arrow>
            <Avatar
              src={imageUrl || undefined}
              sx={{
                width: 48, height: 48,
                bgcolor:
                  !profile.localImagePath || profile.localImagePath === "Non spécifié"
                    ? getMedusaInspiredColor(profile.nom)
                    : "transparent",
              }}
            >
              {getInitials(profile.nom)}
            </Avatar>
          </Tooltip>

          {/* Nom : clamp 2 lignes + ellipsis */}
          <Box flexGrow={1} minWidth={0}>
                      <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
               
                whiteSpace: "normal",
                lineHeight: 1.2,
              }}
           >
              {profile.nom}
            </Typography>
          </Box>

          <Chip
            label={profile.disponibilite ? "Disponible" : "Non disponible"}
            size="small"
            sx={{
              mr: 1,
              bgcolor: profile.disponibilite ? "#37d079" : "#ffe3e3",
              color: profile.disponibilite ? "#fff" : "#a30000",
              fontWeight: 600,
              fontSize: "0.75rem",
              border: profile.disponibilite ? "none" : "1px solid #a30000",
              borderRadius: "16px",
            }}
          />
        </Box>

        {/* ----------- Score de compatibilité (étoiles) ----------- */}
        {hasScore && (
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <Rating
              value={starValue}
              precision={0.1}
              readOnly
              size="small"
              sx={{ color: "#FFD700" }}
            />
          
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* ------------------ Métadonnées ------------------ */}
        <Box sx={{ flexGrow: 1, width: "100%" }}>
          {metaRows.map(([label, val], idx) => (
            <Fragment key={label}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "110px 1fr",
                  alignItems: "flex-start",
                  fontSize: 14,
                  py: 1,
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography color="text.secondary">{label}</Typography>
                  {label === "Poste" && isLongPoste && (
                    <Tooltip title={expanded ? "Voir moins" : "Voir plus"} arrow>
                      <Box
                        onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }}
                        sx={{
                          ml: 1,
                          width: 22, height: 22, borderRadius: "50%",
                          bgcolor: "rgba(0,0,0,0.1)", color: PRIMARY,
                          fontSize: "0.9rem", fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer",
                          transition: "transform 0.2s, background 0.2s",
                          "&:hover": { transform: "scale(1.25)", bgcolor: "rgba(0,0,0,0.2)" },
                        }}
                      >
                        …
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ textAlign: "right" }}>
                  <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                    {val}
                  </Typography>
                </Box>
              </Box>

              {idx < metaRows.length - 1 && <Divider light sx={{ my: 0.5 }} />}
            </Fragment>
          ))}
        </Box>

        <Divider sx={{ mt: 2, mb: 3 }} />

        {/* ------------------ Compétences ------------------ */}
        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: "auto" }}>
          {skills.map((s, i) => (
            <Chip
              key={i}
              label={s}
              size="small"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                px: 1.5,
                height: 28,
                borderRadius: "16px",
                bgcolor: i % 2 === 0 ? "#e6f1ff" : "#ece8ff",
                color: i % 2 === 0 ? "#007bff" : "#6a4cff",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
            />
          ))}
          {extra > 0 && (
            <Chip
              label={`+${extra}`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                px: 1.5,
                height: 28,
                borderRadius: "16px",
                bgcolor: skills.length % 2 === 0 ? "#e6f1ff" : "#ece8ff",
                color: skills.length % 2 === 0 ? "#007bff" : "#6a4cff",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
            />
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
}
